// Z Production's carton labels (his labelItemInfo / cartonLabelHtml / printCartonLabels), ported:
// black and white, 10 × 7 cm on an A4 sheet of 8 or one per page on a Brother QL roll, printed from
// a page that opens in a new tab and prints itself — his layout and sizes, word for word. Every word
// is in the reader's language; the article's name is followed by its English name, as his French
// label is.
import catalog from "@/data/labelCatalog.json";
import { translateTo, type Locale } from "@/i18n";
import { nameIn, type ArticleItem } from "@/services/articles";
import { CARTON_LOGO } from "@/lib/cartonLogo";

export type CartonFormat = "a4" | "ql102x70" | "ql102x152" | "ql62x100";
// Names and printer help: "carton.fmt.<key>" in the word list.
export const CARTON_FORMATS: Record<CartonFormat, { w?: number; h?: number; cls?: string }> = {
  a4: {},
  ql102x70: { w: 102, h: 70, cls: "f70" },
  ql102x152: { w: 102, h: 152, cls: "f152" },
  ql62x100: { w: 62, h: 100, cls: "f62" },
};

type Kit = { key: string; label: string; items: ArticleItem[] };
export const LABEL_KITS = (catalog as { kits: Kit[] }).kits;
const BY_CODE = new Map<string, { item: ArticleItem; kit: Kit }>();
for (const kit of LABEL_KITS) for (const item of kit.items) if (!BY_CODE.has(item.code)) BY_CODE.set(item.code, { item, kit });
export const knownCode = (raw: string) => [...BY_CODE.keys()].find((c) => c.toUpperCase() === raw.toUpperCase()) ?? null;

export type LabelInfo = { code: string; kitKey: string; item: ArticleItem | null; fallbackName: string };
// His labelItemInfo: the article, its kit, and the category printed at the top.
export function labelInfo(code: string, fallbackName?: string | null): LabelInfo {
  const hit = BY_CODE.get(code);
  return { code, kitKey: hit?.kit.key ?? "", item: hit?.item ?? null, fallbackName: fallbackName || code };
}
function category(info: LabelInfo, l: Locale) {
  if (/^KL\d/.test(info.kitKey)) return translateTo(l, "carton.catLinen").toUpperCase();
  const kit = LABEL_KITS.find((k) => k.key === info.kitKey);
  return kit ? kit.label.toUpperCase() : "MYOTAKU";
}

// His cleanSupplierRef: a reference read with its supplier's prefix ("ROTEL-U6744CH") prints without it.
export const cleanSupplierRef = (ref: string | null | undefined) => String(ref || "").trim().replace(/^[A-Za-z]+-(?=[A-Za-z]*\d)/, "");
export type SupplierRefs = Record<string, { ref: string; supplier: string; from_catalog: boolean }[]>;
// His supplierRefFor: the references of this supplier when the reception names one, at most two.
export function supplierRefFor(byCode: SupplierRefs | undefined, code: string, supplier: string | null | undefined) {
  const list = byCode?.[code] ?? [];
  if (!list.length) return "";
  const norm = (t: string) => t.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
  const sup = norm(supplier || ""), first = sup.split(" ")[0] || "";
  const match = first ? list.filter((e) => { const n = norm(e.supplier); return n && (n.includes(first) || sup.includes(n.split(" ")[0])); }) : [];
  return (match.length ? match : list).slice(0, 2).map((e) => cleanSupplierRef(e.ref)).join(" / ");
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string));

export type CartonLine = { info: LabelInfo; sheets: number; qty: string; supRef: string; single?: boolean };

function labelHtml(line: CartonLine, footer: string, l: Locale) {
  const t = (k: string) => translateTo(l, k);
  const qty = line.qty.trim(), sup = line.supRef.trim(), code = line.info.code;
  const it = line.info.item;
  const name = it ? nameIn(it, l) : line.info.fallbackName;
  const english = it && l !== "en" ? it.nameEn : "";
  return `<div class="lab"><div class="lab-head"><div class="lab-cat">${esc(category(line.info, l))}</div>`
    + `<img class="lab-logo" src="${CARTON_LOGO}" alt=""></div>`
    + `<div class="lab-mid"><span class="lab-ref${qty && code.length >= 8 ? " xl" : qty && code.length >= 6 ? " l" : ""}">${esc(code)}</span>`
    + (qty ? `<span class="lab-qty"><small>${esc(t("carton.qtyReceived").toUpperCase())}</small>${esc(qty)}</span>` : "") + "</div>"
    + `<div class="lab-fr">${esc(name)}</div>`
    + (english ? `<div class="lab-en">${esc(english)}</div>` : "")
    + (sup ? `<div class="lab-sup">${esc(t("carton.supRef"))} <b>${esc(sup)}</b></div>` : "")
    + (footer ? `<div class="lab-foot">${esc(footer)}</div>` : "")
    + "</div>";
}

// His printCartonLabels: the print page, in his CSS. A4 = 8 identical labels a sheet ("single" =
// one label, top left); a QL format = one label a page at the roll's size.
export function cartonLabelsDocument(lines: CartonLine[], footer: string, title: string, fmtKey: CartonFormat, l: Locale) {
  const f = CARTON_FORMATS[fmtKey];
  let sheets = "";
  for (const ln of lines) {
    for (let i = 0; i < ln.sheets; i++) {
      const one = labelHtml(ln, footer, l);
      sheets += f.w ? `<div class="pg">${one}</div>` : `<div class="sheet">${ln.single ? one : one.repeat(8)}</div>`;
    }
  }
  if (!sheets) return null;
  const qlCss = f.w ? (`@page{size:${f.w}mm ${f.h}mm;margin:0}`
    + `.pg{width:${f.w}mm;height:${f.h}mm;padding:1.5mm;display:flex;page-break-after:always;break-after:page;overflow:hidden}`
    + ".pg:last-child{page-break-after:auto;break-after:auto}.pg .lab{flex:1;margin:0}"
    + ".f152 .lab{padding:5mm 5mm}.f152 .lab-cat{font-size:13pt}.f152 .lab-logo{width:18mm;height:12mm}.f152 .lab-mid{margin:12mm 0 6mm}"
    + ".f152 .lab-ref{font-size:52pt;padding:2.5mm 4mm}.f152 .lab-fr{font-size:22pt}.f152 .lab-en{font-size:15pt}.f152 .lab-foot{font-size:10pt}"
    + ".f152 .lab-mid{flex-wrap:wrap;gap:4mm}.f152 .lab-ref.l,.f152 .lab-ref.xl{font-size:44pt}.f152 .lab-qty{font-size:34pt;padding:2mm 4mm}.f152 .lab-qty small{font-size:9pt}.f152 .lab-sup{font-size:15pt;margin-top:4mm}.f152 .lab-sup b{font-size:17pt}"
    + ".f62 .lab{padding:2.5mm 3mm}.f62 .lab-cat{font-size:7.5pt}.f62 .lab-logo{width:10mm;height:7mm}.f62 .lab-mid{margin:4mm 0 2mm}"
    + ".f62 .lab-ref{font-size:28pt}.f62 .lab-fr{font-size:13pt}.f62 .lab-en{font-size:10pt}.f62 .lab-foot{font-size:7pt}"
    + ".f62 .lab-mid{flex-wrap:wrap;gap:2mm}.f62 .lab-ref.l{font-size:24pt}.f62 .lab-ref.xl{font-size:20pt}.f62 .lab-qty{font-size:16pt}.f62 .lab-qty small{font-size:5.5pt}.f62 .lab-sup{font-size:9pt}.f62 .lab-sup b{font-size:10pt}"
    + "@media screen{body{background:#ddd}.pg{background:#fff;margin:6mm auto;box-shadow:0 2px 8px rgba(0,0,0,.2)}}") : "";
  const css = (f.w ? "" : "@page{size:A4;margin:0}") + "*{box-sizing:border-box}html,body{margin:0;padding:0;background:#fff;color:#000;font-family:Arial,Helvetica,sans-serif}"
    + ".sheet{width:210mm;height:296mm;padding:8mm 5mm;display:grid;grid-template-columns:100mm 100mm;grid-template-rows:repeat(4,70mm);page-break-after:always;break-after:page;overflow:hidden}"
    + ".sheet:last-child{page-break-after:auto;break-after:auto}"
    + ".lab{margin:1mm;border:0.6mm solid #000;border-radius:2mm;padding:3mm 4mm;display:flex;flex-direction:column;overflow:hidden;position:relative}"
    + ".lab-head{display:flex;align-items:flex-start;gap:3mm}"
    + ".lab-cat{flex:1;font-size:9pt;font-weight:800;letter-spacing:0.02em;padding-bottom:1.2mm;border-bottom:0.4mm solid #000;line-height:1.15}"
    + ".lab-logo{width:12mm;height:8mm;object-fit:cover;border:0.4mm solid #000;border-radius:1.5mm;filter:grayscale(1) contrast(1.2);flex:none}"
    + ".lab-mid{display:flex;align-items:center;gap:3mm;margin:4mm 0 2.5mm}"
    + ".lab-ref{font-size:40pt;font-weight:800;line-height:1;border:0.8mm solid #000;border-radius:2mm;padding:1.5mm 3mm;white-space:nowrap}"
    + ".lab-qty{display:flex;flex-direction:column;align-items:center;line-height:1;font-size:22pt;font-weight:800;background:#000;color:#fff;border-radius:2mm;padding:1.2mm 3mm 1.5mm;white-space:nowrap;-webkit-print-color-adjust:exact;print-color-adjust:exact}"
    + ".lab-ref.l{font-size:32pt}.lab-ref.xl{font-size:26pt}"
    + ".lab-qty small{font-size:6.5pt;font-weight:800;letter-spacing:0.06em;margin-bottom:0.8mm}"
    + ".lab-sup{font-size:10.5pt;margin-top:1.5mm;line-height:1.2;overflow-wrap:anywhere}.lab-sup b{font-family:'Courier New',monospace;font-size:11.5pt}"
    + ".lab-fr{font-size:15pt;font-weight:800;line-height:1.15}"
    + ".lab-en{font-size:11pt;font-style:italic;color:#444;margin-top:0.8mm}"
    + ".lab-foot{margin-top:auto;font-size:7.5pt;color:#444;border-top:0.2mm solid #999;padding-top:1mm}"
    + "@media screen{body{background:#ddd}.sheet{background:#fff;margin:6mm auto;box-shadow:0 2px 8px rgba(0,0,0,.2)}}" + qlCss;
  return `<!doctype html><html lang="${l}"><head><meta charset="utf-8"><title>${esc(title)}</title><style>${css}</style></head>`
    + `<body class="${f.cls || ""}">${sheets}<script>window.onload=function(){setTimeout(function(){window.print();},250);};<\/script></body></html>`;
}

// His openTab: the page opens in a new tab and prints itself. False when the browser blocked it.
export function openPrintTab(doc: string) {
  const url = URL.createObjectURL(new Blob([doc], { type: "text/html" }));
  const win = window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
  return !!win;
}
